// Lib
import { useEffect, useRef, useState } from 'react';
import { pure } from '../../../node_module_clones/recompose';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useTranslation } from 'react-i18next';
import type { GenericReduxDispatch } from '../../../common/actions/actionTypes';

// Config
import getClientConfig from '../../utils/getClientConfig';

// Utils
import { enableAmplitudeTracking, initialiseAmplitude, initialiseAmplitudeData } from '../../analytics/amplitudeService';
import {
    sendCompletedMilestoneEventsThunk,
    shouldSendCompletedMilestonesForAnalyticsChange,
} from '../../analytics/elementCreateMilestoneUtils';
import {
    addCookieBannerPageOverlay,
    cookieGroupAllowed,
    getConsentSettings,
    removeCookieBannerPageOverlay,
} from './oneTrustUtils';
import {
    getCurrentUserId,
    getCurrentUserOTToken,
    selectHasAuthenticatedCurrentUser,
    isGuestSelector,
    selectUserLocalePreferenceOrNull,
} from '../../user/currentUserSelector';
import useOneTrustLoader from './useOneTrustLoader';
import { hideOneTrustBanner, setOneTrustConsentModel, setOneTrustStatus, showOneTrustBanner } from './oneTrustActions';
import { activeModalIdSelector } from '../../components/modal/activeModalSelector';
import usePrevious from '../../utils/react/usePrevious';
import useThrottledCallback from '../../utils/react/useThrottledCallback';
import { getShowOneTrustBanner } from './oneTrustSelectors';
import useUpdateEffect from '../../utils/react/useUpdateEffect';
import { updateUserConsentSettings } from '../../user/userService';
import { getUserAnalytics } from '../../user/userAnalyticsSelector';
import localStorageService from '../../utils/services/localStorage/localStorage';
import { getIsSheetOpen } from '../../mobile/structural/sheet/sheetSelectors';
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';
import { useEnsureModalInteraction } from '@milanote/roma/utils';
import globalLogger from '../../logger';

// Constants
import { OneTrustConsentModel, OneTrustStatus } from './oneTrustTypes';
import { CookieGroups } from '../../../common/oneTrust/oneTrustTypes';
import { OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE } from '../../../common/utils/cookie/mnCookieConstants';
import { MNUserAnalytics } from '../../../common/users/userAnalyticsTypes';
import { syncEngagementTracking } from '../../analytics/engagementService';
import { DEFAULT_LOCALE } from '../../../common/cms/cmsLocaleConstants';

const logger = globalLogger.createChannel('oneTrust.init');

const GUEST_USER_ID = 'guest';

const mapStateToProps = createStructuredSelector({
    isGuest: isGuestSelector,
    hasAuthenticatedCurrentUser: selectHasAuthenticatedCurrentUser,
    userId: getCurrentUserId,
    userOTToken: getCurrentUserOTToken,
    userLocalePreference: selectUserLocalePreferenceOrNull,
    activeModalId: activeModalIdSelector,
    showOneTrustBanner: getShowOneTrustBanner,
    isSegmentPickerSheetOpen: getIsSheetOpen(SheetId.SegmentPicker),
    userAnalytics: getUserAnalytics,
});

const mapDispatchToProps = (dispatch: GenericReduxDispatch) => ({
    dispatchSetOneTrustStatus: (status: OneTrustStatus) => dispatch(setOneTrustStatus(status)),
    dispatchSetOneTrustConsentModel: (consentModel: OneTrustConsentModel | null) =>
        dispatch(setOneTrustConsentModel(consentModel)),
    dispatchShowOneTrustBanner: () => dispatch(showOneTrustBanner()),
    dispatchHideOneTrustBanner: () => dispatch(hideOneTrustBanner()),
    dispatchSendCompletedMilestoneEvents: () => dispatch(sendCompletedMilestoneEventsThunk()),
});

type Props = {
    isGuest: boolean;
    hasAuthenticatedCurrentUser: boolean;
    userId: string;
    userOTToken: string;
    userLocalePreference: string | null;
    activeModalId: string | null;
    showOneTrustBanner: boolean;
    isSegmentPickerSheetOpen: boolean;
    userAnalytics?: MNUserAnalytics;
    dispatchSetOneTrustStatus: (status: OneTrustStatus) => void;
    dispatchSetOneTrustConsentModel: (consentModel: OneTrustConsentModel | null) => void;
    dispatchShowOneTrustBanner: () => void;
    dispatchHideOneTrustBanner: () => void;
    dispatchSendCompletedMilestoneEvents: () => void;
};

const OneTrustInit = pure((props: Props) => {
    const {
        isGuest,
        hasAuthenticatedCurrentUser,
        userId,
        userOTToken,
        userLocalePreference,
        activeModalId,
        showOneTrustBanner,
        isSegmentPickerSheetOpen,
        userAnalytics,
        dispatchSetOneTrustStatus,
        dispatchSetOneTrustConsentModel,
        dispatchShowOneTrustBanner,
        dispatchHideOneTrustBanner,
        dispatchSendCompletedMilestoneEvents,
    } = props;

    const config = getClientConfig();
    const { i18n } = useTranslation();
    const activeAppLocale = userLocalePreference || i18n.resolvedLanguage || i18n.language;

    const [oneTrustState] = useOneTrustLoader({
        isGuest,
        hasAuthenticatedCurrentUser,
        userId,
        userOTToken,
        userLocalePreference: userLocalePreference || DEFAULT_LOCALE,
    });
    const [performanceCookiesAllowed, setPerformanceCookiesAllowed] = useState(() =>
        cookieGroupAllowed(CookieGroups.PERFORMANCE_COOKIES),
    );
    const previousUserAnalyticsRef = useRef<MNUserAnalytics | undefined>(undefined);
    const currentUserConsentSyncDetailsRef = useRef({ isGuest, userId });
    currentUserConsentSyncDetailsRef.current = { isGuest, userId };
    const prevShowOneTrustBanner = usePrevious(showOneTrustBanner);

    const shouldShowCookieBannerOverlay = () => {
        if (!showOneTrustBanner) return false;
        // The segment picker sheet always needs the overlay regardless of opt-in/opt-out status
        // as the banner sits over the sheet and must be blocked
        if (isSegmentPickerSheetOpen) return true;

        const consentModel = window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
        const isOptInConsentModel = consentModel === OneTrustConsentModel.OPT_IN;
        return isOptInConsentModel && activeModalId === null;
    };

    const showCookieBannerOverlay = shouldShowCookieBannerOverlay();
    const prevShowCookieBannerOverlay = usePrevious(showCookieBannerOverlay);

    /**
     * Determine whether or not to show the OneTrust banner
     *
     * The banner is shown only while the OneTrust alert box remains open
     *
     * For the current consent models:
     * - OPT_IN regions show the banner across the app, including on modal pages
     * - non-OPT_IN regions show the banner only on non-modal pages
     *
     * NOTE: This logic is currently driven by the consent model. It may need to change for
     * consent flows that do not map neatly to these modal/non-modal rules
     */
    const checkShouldShowOneTrustBannerInner = useThrottledCallback<[], void>(
        () => {
            if (oneTrustState !== OneTrustStatus.INITIALISED) return;

            // For now, use consent model to determine whether consent banner needs to be displayed.
            // This might change in the future, e.g. CCPA banner would need to be shown even when its consent is opt-out.
            const consentModel = window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
            const isOptInConsentModel = consentModel === OneTrustConsentModel.OPT_IN;

            const shouldShowOneTrustBanner =
                window.OneTrust?.IsAlertBoxClosed?.() === false &&
                (isOptInConsentModel || (!isOptInConsentModel && activeModalId === null));

            if (shouldShowOneTrustBanner) {
                dispatchShowOneTrustBanner();
            } else {
                dispatchHideOneTrustBanner();
            }
        },
        50,
        [oneTrustState, activeModalId, dispatchShowOneTrustBanner, dispatchHideOneTrustBanner],
    );
    // Wrap to satisfy hook's single-arg signature at callsites where we need zero-args
    const checkShouldShowOneTrustBanner = () => checkShouldShowOneTrustBannerInner();

    const updateUserConsentSettingsOnServer = () => {
        const currentUserConsentSyncDetails = currentUserConsentSyncDetailsRef.current;
        const currentUserId = currentUserConsentSyncDetails.userId;

        if (!currentUserId || currentUserConsentSyncDetails.isGuest || currentUserId === GUEST_USER_ID) return;

        const consentSettings = getConsentSettings();
        if (consentSettings) updateUserConsentSettings(consentSettings);
    };

    /**
     * When OneTrust is initialised, also initialise some Redux state, and determine whether or not
     * to show the cookie banner.
     */
    useEffect(() => {
        dispatchSetOneTrustStatus(oneTrustState);

        if (oneTrustState !== OneTrustStatus.INITIALISED) return;

        const consentModel = (window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name ??
            null) as OneTrustConsentModel | null;
        dispatchSetOneTrustConsentModel(consentModel);

        checkShouldShowOneTrustBanner();
        setPerformanceCookiesAllowed(cookieGroupAllowed(CookieGroups.PERFORMANCE_COOKIES));

        // If there are any updates to consent settings external to app.milanote.com (such as from milanote.com),
        // make sure that we sync the latest consent settings to the server.
        const prevOnetrustActiveGroups = localStorageService.getItem(OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE);
        if (window.OnetrustActiveGroups !== prevOnetrustActiveGroups) {
            localStorageService.setItem(OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE, window.OnetrustActiveGroups || '');
            updateUserConsentSettingsOnServer();
        }

        window.OneTrust?.OnConsentChanged?.(() => {
            checkShouldShowOneTrustBanner();
            setPerformanceCookiesAllowed(cookieGroupAllowed(CookieGroups.PERFORMANCE_COOKIES));
            dispatchSendCompletedMilestoneEvents();

            localStorageService.setItem(OT_LAST_ACTIVE_GROUPS_LOCAL_STORAGE, window.OnetrustActiveGroups || '');
            updateUserConsentSettingsOnServer();
        });
    }, [oneTrustState]);

    /**
     * OneTrust can initialise before user analytics hydrate, so send completed milestones
     * when they first become available after consent handling is ready.
     */
    useEffect(() => {
        if (oneTrustState !== OneTrustStatus.INITIALISED) return;

        // Only send when completed milestones first appear; tracker deletes from sending also update analytics.
        const shouldSendCompletedMilestones = shouldSendCompletedMilestonesForAnalyticsChange(
            previousUserAnalyticsRef.current,
            userAnalytics,
        );
        previousUserAnalyticsRef.current = userAnalytics;

        if (!shouldSendCompletedMilestones) return;

        dispatchSendCompletedMilestoneEvents();
    }, [oneTrustState, userAnalytics]);

    /**
     * When changing between non-modal and modal pages, check whether or not to show the OneTrust banner.
     * This is because in some geographical location, the banner is shown on non-modal pages, but not on modal pages.
     */
    useUpdateEffect(() => {
        if (showOneTrustBanner) {
            checkShouldShowOneTrustBanner();
        } else {
            // We use a timeout here to wait for the consent information to be fully synced before checking
            // whether to SHOW the banner.
            setTimeout(() => checkShouldShowOneTrustBanner(), 3000);
        }
    }, [activeModalId]);

    /**
     * Keep the body class in sync with the OneTrust banner visibility
     */
    useEffect(() => {
        if (!prevShowOneTrustBanner && showOneTrustBanner) {
            document.body.classList.add('show-cookie-banner');
        }

        if (prevShowOneTrustBanner && !showOneTrustBanner) {
            document.body.classList.remove('show-cookie-banner');
        }
    }, [prevShowOneTrustBanner, showOneTrustBanner]);

    /**
     * Keep the page overlay in sync with the derived overlay visibility
     */
    useEffect(() => {
        if (!prevShowCookieBannerOverlay && showCookieBannerOverlay) {
            addCookieBannerPageOverlay();
        }

        if (prevShowCookieBannerOverlay && !showCookieBannerOverlay) {
            removeCookieBannerPageOverlay();
        }
    }, [prevShowCookieBannerOverlay, showCookieBannerOverlay]);

    // Ensure OneTrust banner remains interactive when shown on top of a React Aria modal
    useEnsureModalInteraction({
        selector: '#onetrust-consent-sdk',
    });

    /**
     * When performanceCookiesAllowed value change, determine whether or not to enable/disable analytics
     */
    useEffect(() => {
        if (performanceCookiesAllowed === null) return;

        if (performanceCookiesAllowed) {
            initialiseAmplitude(config);
            enableAmplitudeTracking(true);
            initialiseAmplitudeData(config);
            return;
        }

        enableAmplitudeTracking(false);
    }, [performanceCookiesAllowed]);

    useEffect(() => {
        let isCancelled = false;

        const syncAnalytics = async () => {
            if (performanceCookiesAllowed === null || isCancelled) return;

            await syncEngagementTracking({
                isEnabled: performanceCookiesAllowed,
                isGuest,
                userId,
                locale: activeAppLocale,
                config,
            });
        };

        void syncAnalytics().catch(() => {
            logger.warn('Failed to sync analytics');
        });

        return () => {
            isCancelled = true;
        };
    }, [activeAppLocale, performanceCookiesAllowed, isGuest, userId]);

    return null;
});

export default connect(mapStateToProps, mapDispatchToProps)(OneTrustInit);
